"use client";

import type { FontWeight, TextSize } from "@homeinstead/schemas";
import type { ButtonColorVariant } from "./button-styles";
import type {
  LeadFormFieldChangeHandler,
  RadioOption,
  SelectionIndicatorStyle,
} from "./form-types";
import type { StepInputRendererProps } from "./StepInputRenderer";

import { cn } from "@homeinstead/ui";
import { useId } from "react";

const RADIO_MAX_WIDTH_CLASS: Record<string, string> = {
  "2xs": "max-w-[16rem]",
  full: "",
  sm: "max-w-sm",
  xs: "max-w-xs",
};

function getRadioMaxWidthClass(value: string | null | undefined): string {
  if (value && value in RADIO_MAX_WIDTH_CLASS) {
    return RADIO_MAX_WIDTH_CLASS[value];
  }
  return RADIO_MAX_WIDTH_CLASS.full;
}

import {
  resolveFieldsetLegendClasses,
  resolveLabelFontFamilyClass,
  resolveTextSizeClass,
} from "./form-item-styles";
import { defaultHeroVariantStyles } from "./hero-variant-styles";
import { isRadioInput } from "./is-radio-input";
import { RadioInputOption } from "./RadioInputOption";
import { SelectionIndicatorSlot } from "./SelectionIndicatorSlot";
import { usePersistedFieldState } from "./use-persisted-field-state";
import { useTrackLeadFormEvent } from "./useTrackLeadFormEvent";

function RadioInput({
  input,
  onContinue,
  onFieldChange,
  stepName,
  heroVariantStyles,
}: StepInputRendererProps) {
  const trackLeadForm = useTrackLeadFormEvent();

  if (!isRadioInput(input)) {
    return null;
  }

  const {
    buttonLabel: label,
    colorVariant,
    fontWeight,
    helperText,
    textSize,
    autoAdvance,
    options,
    fieldName,
    selectionIndicator,
  } = input;

  const maxWidth =
    "maxWidth" in input
      ? (input as typeof input & { maxWidth?: string | null }).maxWidth
      : undefined;

  const helperTextSize =
    "helperTextSize" in input
      ? (input as typeof input & { helperTextSize?: string | null })
          .helperTextSize
      : undefined;

  return (
    <RadioInputInner
      autoAdvance={autoAdvance}
      colorVariant={colorVariant}
      fieldName={fieldName}
      fontWeight={fontWeight}
      helperText={helperText}
      helperTextSize={helperTextSize}
      heroVariantStyles={heroVariantStyles}
      label={label}
      maxWidth={maxWidth}
      onContinue={onContinue}
      onFieldChange={onFieldChange}
      onSelect={(option) => {
        // Only fire on selection for auto-advance steps. Non-auto-advance steps
        // fire "Lead Form Step" via the Continue button with the persisted value.
        if (!autoAdvance) {
          return;
        }
        const resolved = option.metricsLabel ?? option.value ?? option.label;
        if (stepName && fieldName && resolved) {
          trackLeadForm?.leadFormStep({
            stepName,
            userSelection: { [fieldName]: resolved },
          });
        }
      }}
      options={options}
      selectionIndicator={selectionIndicator}
      textSize={textSize}
    />
  );
}

function RadioInputInner({
  label,
  colorVariant,
  fontWeight,
  helperText,
  helperTextSize,
  maxWidth,
  textSize,
  autoAdvance,
  options,
  fieldName,
  onContinue,
  onFieldChange,
  onSelect: onSelectAnalytics,
  selectionIndicator,
  heroVariantStyles,
}: {
  label: string | null;
  colorVariant: ButtonColorVariant | null;
  fontWeight: FontWeight | null;
  helperText: string | null;
  helperTextSize?: string | null;
  maxWidth?: string | null;
  textSize: TextSize | null;
  autoAdvance: boolean | null;
  options: RadioOption[];
  fieldName: string | null;
  onContinue: () => void;
  onFieldChange?: LeadFormFieldChangeHandler;
  onSelect?: (option: RadioOption) => void;
  selectionIndicator: SelectionIndicatorStyle | null;
  heroVariantStyles?: StepInputRendererProps["heroVariantStyles"];
}) {
  const [selectedValue, selectValue] = usePersistedFieldState(
    fieldName,
    onFieldChange,
  );
  const helperId = useId();

  const handleSelect = (option: RadioOption) => {
    const value = option.value ?? option.label;
    onSelectAnalytics?.(option);
    selectValue(value ?? "");

    if (autoAdvance) {
      onContinue();
    }
  };

  return (
    <fieldset
      aria-describedby={helperText ? helperId : undefined}
      className={cn(
        "flex flex-col gap-4 border-none p-0",
        getRadioMaxWidthClass(maxWidth),
      )}
      role="radiogroup"
    >
      {label && (
        <legend
          className={cn(
            "text-left",
            resolveFieldsetLegendClasses({
              fontWeight,
              textSize,
            }),
          )}
        >
          {label}
        </legend>
      )}
      {helperText && (
        <p
          className={cn(
            // Breathing room before option pills (Bottom Padding in Studio applies below the whole input, not here).
            "mb-6",
            heroVariantStyles?.helperTextColorClass ??
              defaultHeroVariantStyles.helperTextColorClass,
            resolveLabelFontFamilyClass(),
            resolveTextSizeClass(helperTextSize as TextSize | null, "sm"),
          )}
          id={helperId}
        >
          {helperText}
        </p>
      )}
      <div className="flex flex-col gap-3">
        {options.map((option) => {
          const value = option.value ?? option.label;
          const isSelected = value != null && selectedValue === value;
          return (
            <SelectionIndicatorSlot
              gapClass="gap-5"
              isSelected={isSelected}
              key={option._key}
              style={selectionIndicator}
            >
              <RadioInputOption
                colorVariant={colorVariant}
                isSelected={isSelected}
                name={fieldName ?? ""}
                onChange={() => handleSelect(option)}
                option={option}
              />
            </SelectionIndicatorSlot>
          );
        })}
      </div>
    </fieldset>
  );
}

export { RadioInput };
